import React, { useState, useContext, useCallback, useEffect, useRef } from "react"
import { UserContext } from "../../../context/userContext"
import axios from "axios"
import { telerecoveryApiUrl } from "../../../config/microservicesUrls"
import { LoaderMedium } from "../../../components/Loaders"
import { Button } from "primereact/button"
import { Checkbox } from "primereact/checkbox"
import { Toast } from "primereact/toast"
import { Dropdown } from 'primereact/dropdown'
import trcss from "../../../styles/Telerecovery.module.css"
import { meetingProviderOptions, messageChannelOptions, getMessageChannelText, getSelectedChannels } from "../Types/TypeUtils"
import { showError, getActualUTCTime, atLeastOneChannelSelected } from "../Utils"
import isParticipantActive from "../../../helpers/isParticipantActive"
import { InputTextarea } from 'primereact/inputtextarea'
import { useLocation, useNavigate } from 'react-router-dom'
import { Dialog } from 'primereact/dialog'
import MeetingHistory from "./MeetingHistory"
import { OverlayPanel } from 'primereact/overlaypanel'
import { Message } from 'primereact/message'
import ExternalLinkPopup from "../ExternalLinkPopup"
import { MeetingProvider } from "../Types/Enums"
import NoData from "../NoData"
import { LogError } from "../../../helpers/logger"

const api_url = telerecoveryApiUrl()

export default function TRVideoIndex(props) {
    const userCtx = useContext(UserContext)
    const { user } = userCtx.session
    const [loadingIntegrationSettings, setLoadingIntegrationSettings] = useState(true)
    const [loadingHistory, setLoadingHistory] = useState(true)
    const [generatingLink, setGeneratingLink] = useState(false)
    const [unlinkingZoomAccount, setUnlinkingZoomAccount] = useState(false)
    const [sendingInvite, setSendingInvite] = useState(false)
    const [provider, setProvider] = useState(null)
    const [integrationSettings, setIntegrationSettings] = useState(null)
    const [videoServiceResponse, setVideoServiceResponse] = useState(null)
    const [channels, setChannels] = useState(messageChannelOptions())
    const [inviteMessage, setInviteMessage] = useState("")
    const [meetingHistory, setMeetingHistory] = useState(null)
    const [hasZoomToken, setHasZoomToken] = useState(true)
    const [zoomConcentDialogVisible, setZoomConcentDialogVisible] = useState(false)
    const toast = useRef(null)
    const last = 5
    const overlayPanel = useRef(null)
    const [showExternalLinkPopup, setShowExternalLinkPopup] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()
    const searchParams = new URLSearchParams(location.search)
    const [errorQueryParam, setErrorQueryParam] = useState(searchParams.get('error'))

    const isParticipantInactive = !isParticipantActive(userCtx.participant_membership_status)

    const getIntegrationSettings = useCallback(() => {
        const enterpriseId = user.enterprise_id
        const organizationId = user.organization_id
        if (enterpriseId) {
            setLoadingIntegrationSettings(true)
            const config = {
                headers: {
                    Authorization: `Bearer ${userCtx.tokenTelerecovery}`,
                }
            }
            axios
                .get(`${api_url}/api/video/settings/${enterpriseId}/${organizationId}`, config)
                .then((response) => {
                    if (response.status === 200) {
                        let data = response.data
                        setIntegrationSettings(data)
                        setProvider(data.preferedProvider)
                    }
                    setLoadingIntegrationSettings(false)
                })
                .catch((response) => {
                    setLoadingIntegrationSettings(false)
                    const error = response["response"]["data"]["error"]
                    showError(toast, "Not able to get settings", error)
                })
        }
    }, [userCtx.tokenTelerecovery, user.enterprise_id, user.organization_id])

    useEffect(() => {
        setInviteMessage(`${user.staff_name_first} ${user.staff_name_last} has initiated a video session with you.`)
    }, [user.staff_name_first, user.staff_name_last])

    useEffect(() => {
        if (integrationSettings && integrationSettings.meetingServicesConfigurations) {
            const zoomEnabled = integrationSettings.meetingServicesConfigurations.filter(c => c.provider === MeetingProvider.Zoom)[0].isEnabled
            if (zoomEnabled) {
                const config = {
                    headers: {
                        Authorization: `Bearer ${userCtx.tokenTelerecovery}`,
                    }
                }
                const enterpriseId = user.enterprise_id
                const organizationId = user.organization_id
                const staffId = user.staff_id
                const url = `${api_url}/zoom/hasToken?enterpriseId=${enterpriseId}&organizationId=${organizationId}&staffId=${staffId}`
                axios
                    .get(url, config)
                    .then((response) => {
                        if (response.status === 200) {
                            setHasZoomToken(response.data)
                        }
                    })
                    .catch((response) => {
                        LogError("Error while checking zoom token", response)
                        const error = response["response"]["data"]["error"]
                        showError(toast, "Error while getting zoom token", error)
                    })
            }
        }
    }, [integrationSettings])

    const getMeetingHistory = useCallback(() => {
        setLoadingHistory(true)
        const config = {
            headers: {
                Authorization: `Bearer ${userCtx.tokenTelerecovery}`,
            }
        }
        const enterpriseId = user.enterprise_id
        const organizationId = user.organization_id
        const staffId = user.staff_id
        axios
            .get(`${api_url}/api/video/history?enterpriseId=${enterpriseId}&organizationId=${organizationId}&staffId=${staffId}&participantId=${userCtx.participant_id}&last=${last}`,
                config)
            .then((response) => {
                if (response.status === 200) {
                    let meetings = response.data?.meetings
                    let errors = response.data?.errors
                    if (errors && errors.length > 0) {
                        LogError("Errors while getting meeting history", errors)
                        toast.current.show({
                            severity: "warn",
                            detail: "Some meeting details couldn't be retrieved. Contact support if the problem persists.",
                            life: 3500,
                        })
                    }
                    setMeetingHistory(meetings)
                }
                setLoadingHistory(false)
            })
            .catch((response) => {
                setLoadingHistory(false)
                const error = response["response"]["data"]["error"]
                showError(toast, "Not able to get history", error)
            })
    }, [userCtx.tokenTelerecovery, user.enterprise_id, user.organization_id, user.staff_id, userCtx.participant_id])

    useEffect(() => {
        getIntegrationSettings()
        getMeetingHistory()
    }, [getIntegrationSettings, getMeetingHistory])

    const removeZoomToken = useCallback(() => {
        const enterpriseId = user.enterprise_id
        const organizationId = user.organization_id
        const staffId = user.staff_id
        const url = `${api_url}/zoom/removeToken/${enterpriseId}/${organizationId}/${staffId}`
        const config = {
            headers: {
                Authorization: `Bearer ${userCtx.tokenTelerecovery}`,
            }
        }
        setUnlinkingZoomAccount(true)
        axios
            .put(url, {}, config)
            .then((response) => {
                if (response.status === 200) {
                    setHasZoomToken(false)
                    setUnlinkingZoomAccount(false)
                    toast.current.show({
                        severity: "info",
                        detail: "Zoom account unlinked",
                        life: 3000,
                    })
                }
            })
            .catch((response) => {
                LogError("Error while unlinking zoom account", response)
                setUnlinkingZoomAccount(false)
                const error = response["response"]["data"]["error"]
                showError(toast, "Error while unlinking account", error)
            })
    }, [userCtx.tokenTelerecovery, user.enterprise_id, user.organization_id, user.staff_id])

    if (loadingIntegrationSettings) {
        return <LoaderMedium></LoaderMedium>
    }

    const errorDialog = () => {
        if (errorQueryParam) {
            console.log(`Error while setting Zoom authentication: ${errorQueryParam}`)
        }
        return <Dialog
            header="Error"
            visible={errorQueryParam !== null}
            style={{ width: '50vw' }}
            className="custom_dialog"
            footer={
                <Button
                    className={trcss.button}
                    label="Ok"
                    icon="pi pi-check"
                    onClick={() => {
                        setErrorQueryParam(null)
                        navigate(location.pathname, { replace: true })
                    }}
                    autoFocus
                />
            }
            modal
            closable={false}
            onHide={() => { }}
        >
            <p>Error while setting Zoom Authentication. Please try again.</p>
        </Dialog>
    }

    const acceptZoomConcent = () => {
        const config = {
            headers: {
                Authorization: `Bearer ${userCtx.tokenTelerecovery}`,
            }
        }
        const enterpriseId = user.enterprise_id
        const organizationId = user.organization_id
        const staffId = user.staff_id
        axios
            .get(`${api_url}/zoom/authorize/${enterpriseId}/${organizationId}/${staffId}`, config)
            .then((response) => {
                if (response.status === 200) {
                    const authorizationUrl = response.data.authorizationUrl
                    if (authorizationUrl) {
                        window.location.href = authorizationUrl
                    }
                }
            })
            .catch((response) => {
                LogError("Error while accepting zoom consent", response)
                const error = response["response"]["data"]["error"]
                showError(toast, "Error getting consent link", error)
            })
    }

    const generateMeetingLink = () => {
        setGeneratingLink(true)
        const config = {
            headers: {
                Authorization: `Bearer ${userCtx.tokenTelerecovery}`,
            }
        }
        const data = {
            service: provider,
            enterpriseId: user.enterprise_id,
            organizationId: user.organization_id,
            organizationName: user.organization_name,
            staffId: user.staff_id,
            staffName: `${user.staff_name_first} ${user.staff_name_last}`,
            participantId: userCtx.participant_id,
            participantName: `${userCtx.participant_name_first} ${userCtx.participant_name_last}`
        }
        axios
            .post(`${api_url}/api/video/createMeetingLink`,
                data,
                config)
            .then((response) => {
                if (response.status === 200) {
                    setVideoServiceResponse(response.data)
                }
                setGeneratingLink(false)
            })
            .catch((response) => {
                LogError("Error while generating meeting link", response)
                setGeneratingLink(false)
                const error = response["response"]["data"]["error"]
                showError(toast, "Not able to generate link", error)
            })
    }

    const sendMeetingInvite = () => {
        if (!atLeastOneChannelSelected(channels)) {
            return
        }
        setSendingInvite(true)
        const config = {
            headers: {
                Authorization: `Bearer ${userCtx.tokenTelerecovery}`,
            }
        }
        const data = {
            service: provider,
            organizationId: user.organization_id,
            organizationAlias: user.organization_alias,
            enterpriseId: user.enterprise_id,
            staffId: user.staff_id,
            staffName: `${user.staff_name_first} ${user.staff_name_last}`,
            participantId: userCtx.participant_id,
            participantEmail: userCtx.participant_email_account,
            meetingId: videoServiceResponse.meetingId,
            joinLink: videoServiceResponse.joinLink,
            passcode: videoServiceResponse.passcode,
            channels: getSelectedChannels(channels),
            inviteMessage: inviteMessage,
        }

        axios
            .post(`${api_url}/api/video/sendMeetingInvite`,
                data,
                config)
            .then((response) => {
                setSendingInvite(false)
                setMeetingHistory(
                    [
                        {
                            sessionStartedAt: getActualUTCTime(),
                            joinLink: data.joinLink,
                            provider: data.service,
                            staffId: user.staff_id
                        }, ...meetingHistory
                    ]
                )
                setVideoServiceResponse(null)
                setChannels(messageChannelOptions())
                setInviteMessage(`${user.staff_name_first} ${user.staff_name_last} has initiated a video session with you.`)
                setProvider(integrationSettings.preferedProvider)
                toast.current.show({
                    severity: "info",
                    detail: "Meeting invite sent",
                    life: 3000,
                })
            })
            .catch((response) => {
                setSendingInvite(false)
                const error = response["response"]["data"]["error"]
                showError(toast, "Not able to send meeting invite", error)
            })
    }

    const showOverlay = (e) => {
        overlayPanel.current.toggle(e)
        setTimeout(() => {
            overlayPanel.current.hide()
        }, 2000)
    }

    if (integrationSettings.preferedProvider === MeetingProvider.None) {
        return (
            <div className={trcss.no_record_div}>
                <NoData />
                <div style={{ width: "415px" }}>
                    <p style={{ fontWeight: "700" }}>
                        There is no video integration available
                    </p>
                    <p style={{ marginTop: "15px" }}>Please contact the administrator to set an integration</p>
                </div>
            </div>
        )
    }

    const zoomConcentDialog = () => {
        return <Dialog
            header="Information"
            visible={zoomConcentDialogVisible}
            style={{ width: '50vw' }}
            className="custom_dialog"
            footer={
                <Button
                    className={trcss.button}
                    label="Ok"
                    icon="pi pi-check"
                    onClick={() => {
                        setZoomConcentDialogVisible(false)
                        acceptZoomConcent()
                    }}
                    autoFocus
                />
            }
            modal
            onHide={() => setZoomConcentDialogVisible(false)}
        >
            <p>You will be redirected to Zoom for authentication and consent</p>
        </Dialog>
    }

    return (
        <>
            <Toast ref={toast} />
            {errorDialog()}
            <div className={trcss.video_index} >
                <div className={trcss.flex_card} style={{ flexDirection: "column", width: "55%" }}>
                    <h3 className={trcss.step_title}>Select provider</h3>
                    <div className={trcss.field}>
                        <label className={`${trcss.label}`}>
                            Provider
                        </label>
                        <Dropdown
                            value={provider}
                            className={trcss.selector}
                            style={{ width: '100%', fontSize: '12px', marginTop: '6px' }}
                            options={
                                meetingProviderOptions(integrationSettings.preferedProvider).filter(p => {
                                    const enabledServices = integrationSettings.meetingServicesConfigurations.filter(c => c.isEnabled)
                                    if (enabledServices.some(s => s.provider == p.value)) {
                                        return p
                                    }
                                })
                            }
                            disabled={isParticipantInactive}
                            panelClassName="custom_dropdown_panel"
                            onChange={(e) => {
                                setProvider(e.value)
                            }}
                            optionLabel="label"
                            optionValue="value"
                        />
                    </div>
                    <div className={trcss.field}>
                        {!videoServiceResponse && provider === MeetingProvider.Zoom && !hasZoomToken &&
                            <>
                                <Button
                                    className={trcss.inverted_button}
                                    label={"Accept Zoom Consent"}
                                    type="submit"
                                    style={{
                                        width: "221px",
                                        height: "42px",
                                        justifyContent: "center",
                                    }}
                                    onClick={() => setZoomConcentDialogVisible(true)}
                                />
                                {zoomConcentDialog()}
                            </>
                        }
                        {!videoServiceResponse && (provider !== MeetingProvider.Zoom || (provider == MeetingProvider.Zoom && hasZoomToken)) &&
                            <div style={{ display: "flex", gap: "10px", justifyContent: "space-between" }}>
                                <Button
                                    className={trcss.inverted_button}
                                    label={!generatingLink ? "Generate meeting link" : "Generating link..."}
                                    type="submit"
                                    style={{
                                        width: "221px",
                                        height: "42px",
                                        justifyContent: "center",
                                    }}
                                    disabled={generatingLink || isParticipantInactive}
                                    loading={generatingLink}
                                    loadingIcon={<i className="pi pi-spin pi-spinner" style={{ order: 1, marginLeft: '0.5em' }}></i>}
                                    onClick={() => generateMeetingLink()}
                                />
                                {(provider == MeetingProvider.Zoom && hasZoomToken) &&
                                    <Button
                                        className={trcss.inverted_button}
                                        label={"Unlink Zoom account"}
                                        type="submit"
                                        style={{
                                            justifyContent: "center",
                                        }}
                                        disabled={unlinkingZoomAccount}
                                        loading={unlinkingZoomAccount}
                                        loadingIcon={<i className="pi pi-spin pi-spinner" style={{ order: 1, marginLeft: '0.5em' }}></i>}
                                        onClick={() => removeZoomToken()}
                                    />
                                }
                            </div>
                        }
                        {videoServiceResponse &&
                            <div className={trcss.join_link}>
                                <label>Join link</label>
                                <div>
                                    <ExternalLinkPopup
                                        link={videoServiceResponse.joinLink}
                                        showPopup={showExternalLinkPopup}
                                        setShowPopup={setShowExternalLinkPopup}
                                    />
                                    <p className={trcss.link} onClick={(e) => { setShowExternalLinkPopup(true) }}>
                                        {videoServiceResponse.joinLink}
                                    </p>
                                    <button onClick={(e) => {
                                        navigator.clipboard.writeText(videoServiceResponse.joinLink)
                                        showOverlay(e)
                                    }} >
                                        <i className="pi pi-copy" />
                                    </button>
                                    <OverlayPanel
                                        ref={overlayPanel}
                                        className={"custom_overlay_panel"}>
                                        Copied to clipboard!
                                    </OverlayPanel>
                                </div>
                            </div>
                        }
                        {videoServiceResponse && videoServiceResponse.passcode &&
                            <p><i>Passcode: </i><b>{videoServiceResponse.passcode}</b></p>
                        }
                    </div>
                    <hr />
                    <p className={trcss.step_title}>Send invite to participant</p>
                    <div className={trcss.field}>
                        <label className={trcss.label}>
                            Invite message
                        </label>
                        <InputTextarea
                            placeholder="Type your message"
                            autoResize
                            style={{ minWidth: "250px", marginTop: "6px" }}
                            className={trcss.newMessageBox}
                            value={inviteMessage}
                            onChange={(e) => setInviteMessage(e.target.value)}
                            rows={2}
                            disabled={isParticipantInactive}
                        />
                        <Message
                            severity="info"
                            text="The meeting link will automatically be added to the invite message when sent"
                            className={trcss.info_message_white}
                            style={{ fontStyle: "italic" }}
                        />
                    </div>
                    <div className={trcss.field}>
                        <label className={trcss.label} style={{ marginBottom: "6px" }}>Message channels</label>
                        {Object.entries(channels).map(([key, value]) => {
                            return (
                                <div className={trcss.chkbox} key={`channel_${key}`} style={{ width: "70px" }}>
                                    <Checkbox
                                        id={`channel_${key}`}
                                        className="custom_checkbox"
                                        value={value}
                                        onChange={(e) => {
                                            setChannels({ ...channels, [key]: e.checked })
                                        }}
                                        checked={value}
                                        disabled={isParticipantInactive}
                                    />
                                    <label htmlFor={`channel_${key}`} style={{ marginLeft: "5px" }}>
                                        {getMessageChannelText(key)}
                                    </label>
                                </div>
                            )
                        })
                        }
                    </div>
                    <Button
                        className={trcss.inverted_button}
                        label={!sendingInvite ? "Send meeting invite" : "Sending invite..."}
                        type="submit"
                        style={{
                            marginTop: "20px",
                            height: "42px",
                            width: "221px",
                            justifyContent: "center",
                        }}
                        disabled={!videoServiceResponse || sendingInvite ||
                            !atLeastOneChannelSelected(channels) ||
                            isParticipantInactive}
                        loading={sendingInvite}
                        loadingIcon={<i className="pi pi-spin pi-spinner" style={{ order: 1, marginLeft: '0.5em' }}></i>}
                        onClick={() => sendMeetingInvite()}
                    />
                </div>
                {loadingHistory ?
                    <div className={trcss.history_loader}>
                        <div style={{ height: "171px", width: "100%" }}></div>
                        <div style={{ height: "436px", width: "100%" }}></div>
                    </div>
                    :
                    <div className={trcss.info_panel}>
                        {meetingHistory && meetingHistory.length === 0 &&
                            <div className={trcss.wrapper}>
                                <p>In this pannel you can see the history of the last 5 meetings</p>
                            </div>
                        }
                        <MeetingHistory
                            meetingHistory={meetingHistory ?? []}
                            last={last}
                        />
                    </div>
                }
            </div >
        </>
    )


}  